import React, { useMemo } from "react";
import {
  buildHeaderRows,
  flattenLeafColumns,
  formatDecimalComma,
  isDecimalInputValid,
  parseDecimalComma,
  PROVISION_TOTAL_ROW,
  canEditBranchRow,
  getComputedCellValue,
} from "../../../models/provisionReport";

const headerBg = "#D6EAF8";
const totalBg = "#D6EAF8";
const lockedBg = "#F5F5F5";

const styles = {
  wrap: {
    overflow: "auto",
    maxHeight: "calc(100vh - 280px)",
    border: "1px solid #c0c0c0",
    background: "#fff",
  },
  table: {
    borderCollapse: "separate",
    borderSpacing: 0,
    minWidth: "100%",
    fontSize: 13,
  },
  th: {
    background: headerBg,
    border: "1px solid #9ab",
    padding: "6px 8px",
    textAlign: "center",
    fontWeight: 600,
    whiteSpace: "nowrap",
    position: "sticky",
    zIndex: 3,
  },
  thSticky: {
    left: 0,
    zIndex: 5,
    minWidth: 220,
    maxWidth: 280,
    textAlign: "left",
  },
  td: {
    border: "1px solid #ccc",
    padding: 0,
    background: "#fff",
    verticalAlign: "middle",
  },
  tdSticky: {
    position: "sticky",
    left: 0,
    zIndex: 2,
    background: "#fff",
    minWidth: 220,
    maxWidth: 280,
    padding: "6px 10px",
    fontWeight: 500,
    border: "1px solid #ccc",
  },
  input: {
    width: "100%",
    border: "none",
    outline: "none",
    padding: "6px 8px",
    boxSizing: "border-box",
    background: "transparent",
    fontSize: 13,
    minWidth: 90,
  },
  totalTd: {
    background: totalBg,
    fontWeight: 700,
    padding: "6px 8px",
    border: "1px solid #9ab",
    textAlign: "right",
  },
};

function sumColumn(rows, key, col) {
  return rows.reduce((acc, row) => {
    if (col?.type === "computed") {
      return acc + (getComputedCellValue(col, row.cells) || 0);
    }
    const v = row.cells?.[key];
    const n = typeof v === "number" ? v : parseDecimalComma(v);
    return acc + (n == null ? 0 : n);
  }, 0);
}

/**
 * columns — активные колонки вкладки (уже с учётом funding mode)
 * rows — строки филиалов (без ИТОГО)
 */
const ProvisionReportGrid = ({
  columns,
  rows,
  onCellChange,
  employee,
  access,
  readOnly = false,
}) => {
  const leaves = useMemo(() => flattenLeafColumns(columns), [columns]);
  const headerRows = useMemo(() => buildHeaderRows(columns), [columns]);
  const depth = headerRows.length;

  const stickyHeaderTop = (level) => level * 32;

  const handleChange = (branchKey, col, raw) => {
    if (col.type === "decimal") {
      if (!isDecimalInputValid(raw)) return;
      onCellChange(branchKey, col.key, raw === "" ? null : raw, col);
      return;
    }
    if (col.type === "number") {
      if (raw !== "" && !/^-?\d*$/.test(String(raw))) return;
      onCellChange(
        branchKey,
        col.key,
        raw === "" ? null : Number(raw),
        col
      );
      return;
    }
    onCellChange(branchKey, col.key, raw === "" ? null : raw, col);
  };

  const displayValue = (value, col) => {
    if (value === null || value === undefined) return "";
    if (col.type === "decimal") {
      if (typeof value === "string") return value;
      return formatDecimalComma(value);
    }
    if (col.type === "number") {
      return value === null || value === undefined ? "" : String(value);
    }
    if (col.type === "date") {
      return value || "";
    }
    return value ?? "";
  };

  return (
    <div style={styles.wrap}>
      <table style={styles.table}>
        <thead>
          {headerRows.map((row, level) => (
            <tr key={`h-${level}`}>
              {level === 0 && (
                <th
                  rowSpan={depth}
                  style={{
                    ...styles.th,
                    ...styles.thSticky,
                    top: 0,
                    height: depth * 32,
                  }}
                >
                  Филиалы
                </th>
              )}
              {row.map((cell) => (
                <th
                  key={`${level}-${cell.key}`}
                  colSpan={cell.colSpan}
                  rowSpan={cell.rowSpan}
                  style={{
                    ...styles.th,
                    top: stickyHeaderTop(level),
                  }}
                >
                  {cell.title}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {rows.map((row) => {
            const editable =
              !readOnly && canEditBranchRow(row, employee, access);
            return (
              <tr key={row.branchKey}>
                <td
                  style={{
                    ...styles.tdSticky,
                    background: editable ? "#fff" : lockedBg,
                    color: editable ? "#000" : "#666",
                  }}
                >
                  {row.branchName}
                </td>
                {leaves.map((col) => {
                  const isComputed = col.type === "computed";
                  const val = isComputed
                    ? getComputedCellValue(col, row.cells)
                    : row.cells?.[col.key];
                  const cellEditable = editable && !isComputed;
                  const bg = isComputed
                    ? headerBg
                    : cellEditable
                    ? "#fff"
                    : lockedBg;
                  return (
                    <td
                      key={`${row.branchKey}-${col.key}`}
                      style={{ ...styles.td, background: bg }}
                    >
                      {cellEditable ? (
                        col.type === "date" ? (
                          <input
                            type="date"
                            style={styles.input}
                            value={displayValue(val, col)}
                            onChange={(e) =>
                              handleChange(row.branchKey, col, e.target.value)
                            }
                          />
                        ) : (
                          <input
                            type="text"
                            style={{
                              ...styles.input,
                              textAlign:
                                col.type === "decimal" || col.type === "number"
                                  ? "right"
                                  : "left",
                            }}
                            value={displayValue(val, col)}
                            onChange={(e) =>
                              handleChange(row.branchKey, col, e.target.value)
                            }
                          />
                        )
                      ) : (
                        <div
                          style={{
                            ...styles.input,
                            textAlign:
                              isComputed ||
                              col.type === "decimal" ||
                              col.type === "number"
                                ? "right"
                                : "left",
                            color: isComputed ? "#000" : "#666",
                            fontWeight: isComputed ? 600 : 400,
                          }}
                        >
                          {isComputed
                            ? formatDecimalComma(val)
                            : displayValue(val, col)}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
          <tr>
            <td
              style={{
                ...styles.tdSticky,
                ...styles.totalTd,
                textAlign: "left",
                zIndex: 2,
              }}
            >
              {PROVISION_TOTAL_ROW.branchName}
            </td>
            {leaves.map((col) => (
              <td key={`total-${col.key}`} style={styles.totalTd}>
                {col.aggregate === "sum" || col.type === "computed"
                  ? formatDecimalComma(sumColumn(rows, col.key, col))
                  : ""}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default ProvisionReportGrid;
